import { isMobileBreakpoint } from '@theme/utilities';

const LP_CONTENT_SELECTORS = [
  '.custom-section .page-width > *',
  '.custom-section .custom-section__inner > *',
  '.main-page .main-page__inner > *',
  '.comparison-table__inner > *',
].join(', ');

function isVisibleElement(element) {
  if (!(element instanceof HTMLElement)) return false;

  const style = getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;

  return element.getBoundingClientRect().height > 0;
}

export function shouldSkipLpNavSection(section) {
  if (!(section instanceof HTMLElement)) return false;
  if (section.classList.contains('lp-nav-skip')) return true;
  if (section.querySelector('.lp-nav-skip')) return true;

  if (
    section.querySelector(
      '.comparison-table, .main-page, .custom-section--fill-viewport, [data-lp-quiz-choice], .custom-section[id="propriétaires"]',
    )
  ) {
    return false;
  }

  const contentBlocks = [...section.querySelectorAll(LP_CONTENT_SELECTORS)].filter(isVisibleElement);
  const hasRichContent = section.querySelector('img, .reviews-badge, [data-lp-quiz-choice], .comparison-table__table');

  if (hasRichContent) return false;

  const sectionHeight = section.getBoundingClientRect().height;
  return contentBlocks.length <= 1 && sectionHeight < window.innerHeight * 0.45;
}

export function shouldCenterLpSection(section) {
  if (!(section instanceof HTMLElement)) return false;
  if (section.querySelector('.custom-section--fill-viewport')) return true;

  return isMobileBreakpoint() && !!section.querySelector('.custom-section[id="propriétaires"]');
}

/**
 * Returns the document Y position to scroll to for an LP section.
 */
export function getLpSectionScrollTop(section, { align } = {}) {
  if (!(section instanceof HTMLElement)) return window.scrollY;

  const resolvedAlign = align ?? (shouldCenterLpSection(section) ? 'center' : 'start');

  if (resolvedAlign === 'center') {
    const rect = section.getBoundingClientRect();
    const sectionTop = rect.top + window.scrollY;
    const centeredTop = sectionTop + rect.height / 2 - window.innerHeight / 2;

    return Math.max(0, centeredTop);
  }

  const candidates = section.querySelectorAll(LP_CONTENT_SELECTORS);
  let top = Infinity;

  for (const candidate of candidates) {
    if (!isVisibleElement(candidate)) continue;

    const candidateTop = candidate.getBoundingClientRect().top + window.scrollY;
    if (candidateTop < top) top = candidateTop;
  }

  if (top !== Infinity) return top;

  const contentRoot = section.querySelector('.custom-section, .main-page, .comparison-table') ?? section;
  return contentRoot.getBoundingClientRect().top + window.scrollY;
}

export function getLpSectionFromElement(element) {
  if (!(element instanceof HTMLElement)) return null;

  return element.closest('.shopify-section') ?? element;
}

export function filterLpNavSections(sections) {
  return sections.filter((section) => !shouldSkipLpNavSection(section));
}

export function getNextLpNavSection(sections, currentIndex) {
  let nextIndex = currentIndex + 1;

  while (nextIndex < sections.length && shouldSkipLpNavSection(sections[nextIndex])) {
    nextIndex++;
  }

  return sections[nextIndex] ?? sections.at(-1) ?? null;
}

function restoreScrollBehavior(html, previousBehavior) {
  if (previousBehavior) {
    html.style.scrollBehavior = previousBehavior;
    return;
  }

  html.style.removeProperty('scroll-behavior');
}

/**
 * Scrolls to an LP section using start or center alignment depending on section type.
 */
export function scrollToLpSection(section, { behavior = 'smooth' } = {}) {
  if (!(section instanceof HTMLElement)) return;

  const html = document.documentElement;
  const previousBehavior = html.style.scrollBehavior;
  html.style.scrollBehavior = 'auto';

  const top = getLpSectionScrollTop(section);
  window.scrollTo({ top, behavior });

  const finish = () => {
    restoreScrollBehavior(html, previousBehavior);
  };

  if (behavior === 'smooth') {
    const fallbackTimeout = window.setTimeout(finish, 700);

    window.addEventListener(
      'scrollend',
      () => {
        window.clearTimeout(fallbackTimeout);
        finish();
      },
      { once: true },
    );
  } else {
    finish();
  }
}
